import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Text from "../../common/typography/text.js";
import AnalyticsToolbar from "./components/AnalyticsToolbar.js";
import ScopedMetricGrid from "./components/ScopedMetricGrid.js";
import PerformanceTrendChart from "./components/PerformanceTrendChart.js";
import ReceivablesSummary from "./components/ReceivablesSummary.js";
import PortfolioExplorer from "./components/PortfolioExplorer.js";
import LeadersPanel from "./components/LeadersPanel.js";
import AABIErrorPanel from "./components/AABIErrorPanel.js";
import { fetchDrillDown } from "./aabiService";
import { useAabiDrillState } from "./useAabiDrillState";
import { buildPeriodParams } from "./aabiPeriods";
import { buildInvoiceDashboardUrl } from "./aabiFilters";
import { LEVELS } from "./aabiConstants";
import "./AABI.css";

// The unified Manufacturer Home analytics workspace (Stage 1 UI upgrade §5.1):
// one scope toolbar, one KPI strip, one trend + receivables row, one portfolio
// explorer, one tabbed Leaders panel — all driven by the single URL-backed
// drill scope (§9.2).
const AABIManufacturerTab = ({ manufacturerId, manufacturerName }) => {
  const navigate = useNavigate();
  const {
    level,
    categoryId,
    groupId,
    productId,
    startDate,
    endDate,
    measure,
    isDefaultUrl,
    goToManufacturer,
    goToCategory,
    goToGroup,
    setMeasure,
    setDateRange,
    reset,
  } = useAabiDrillState();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const load = async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const periodParams = buildPeriodParams({ startDate, endDate });
      const response = await fetchDrillDown({
        manufacturerId,
        manufacturerName,
        level,
        categoryId,
        groupId,
        productId,
        ...periodParams,
      });
      // A stale response (superseded by a faster drill/date change) must never
      // overwrite a newer one (§5.7 race guard).
      if (requestId !== requestIdRef.current) return;
      setData(response);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Failed to fetch AABI drilldown", err);
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manufacturerId, level, categoryId, groupId, productId, startDate, endDate]);

  const context = data?.context ?? {};

  // Canonical names come from the drill response (§8.3) so direct links and
  // refreshes rebuild the breadcrumb without in-memory row state; while the
  // first load is in flight we fall back to the manufacturer name prop.
  const breadcrumbItems = [
    {
      label: context.manufacturerName || manufacturerName || "Manufacturer",
      onClick: level !== LEVELS.MANUFACTURER ? goToManufacturer : undefined,
    },
  ];
  if (context.categoryName) {
    breadcrumbItems.push({
      label: context.categoryName,
      onClick:
        level !== LEVELS.CATEGORY && level !== LEVELS.MANUFACTURER
          ? goToCategory
          : undefined,
    });
  }
  if (context.groupName) {
    breadcrumbItems.push({
      label: context.groupName,
      onClick: level === LEVELS.PRODUCT ? goToGroup : undefined,
    });
  }
  if (context.productName) {
    breadcrumbItems.push({
      label: context.productCode
        ? `${context.productName} (${context.productCode})`
        : context.productName,
    });
  }

  // Canonical-scope invoice handoff (§7.3): exactly the current manufacturer,
  // category, group, product and dates — never a stale deeper key.
  const viewInvoicesUrl = buildInvoiceDashboardUrl({
    manufacturerId,
    categoryId: context.categoryId ?? categoryId,
    groupId: context.groupId ?? groupId,
    productId: context.productId ?? productId,
    startDate,
    endDate,
  });

  const navigateToInvoices = () => navigate(viewInvoicesUrl);

  const handleInvoiceRow = (row) => {
    navigate(
      buildInvoiceDashboardUrl({
        manufacturerId,
        categoryId: context.categoryId ?? categoryId,
        groupId: context.groupId ?? groupId,
        productId: row.id,
        startDate,
        endDate,
      }),
    );
  };

  const productSummary =
    level === LEVELS.PRODUCT
      ? `End of hierarchy — ${context.productName ?? "product"}${
          context.productCode ? ` (${context.productCode})` : ""
        }. Metrics above reflect this product for the selected period.`
      : null;

  return (
    <div className="aabi-manufacturer-tab">
      <h1 className="aabi-visually-hidden">Manufacturer analytics</h1>

      <AnalyticsToolbar
        level={level}
        breadcrumbItems={breadcrumbItems}
        startDate={startDate}
        endDate={endDate}
        measure={measure}
        onMeasureChange={setMeasure}
        onDateRangeChange={setDateRange}
        onReset={reset}
        showReset={!isDefaultUrl}
        viewInvoicesUrl={viewInvoicesUrl}
      />

      {error && !data ? (
        <AABIErrorPanel
          message="Couldn't load Analytics data for this manufacturer."
          onRetry={load}
        />
      ) : (
        <>
          <ScopedMetricGrid
            totals={data?.totals}
            measure={measure}
            loading={loading && !data}
            onViewInvoices={navigateToInvoices}
          />

          <div className="aabi-performance-row">
            <PerformanceTrendChart
              manufacturerId={manufacturerId}
              manufacturerName={manufacturerName}
              scope={{ categoryId, groupId, productId }}
              period={{ startDate, endDate }}
              measure={measure}
            />
            <ReceivablesSummary manufacturerId={manufacturerId} />
          </div>

          <PortfolioExplorer
            manufacturerId={manufacturerId}
            scope={{ categoryId, groupId, productId }}
            rows={data?.rows ?? []}
            totals={data?.totals}
            measure={measure}
            level={level}
            period={buildPeriodParams({ startDate, endDate })}
            loading={loading}
            error={error && Boolean(data)}
            onRetry={load}
            onInvoiceRow={handleInvoiceRow}
            scopeSummary={productSummary}
          />

          <LeadersPanel
            scope={{ manufacturerId, categoryId, groupId, productId }}
            period={buildPeriodParams({ startDate, endDate })}
            measure={measure}
            totals={data?.totals}
          />

          {loading && (
            <Text variant="tinyText" color="lightText" className="aabi-visually-hidden" aria-live="polite">
              Updating analytics for the selected scope…
            </Text>
          )}
        </>
      )}
    </div>
  );
};

export default AABIManufacturerTab;
